import React, { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { Furnishing } from '../../@types/FurnishingType'
import { Button } from '../../components/styled/Button/Button'
import { updateFurnishingType } from '../../store/lead/leadSlice'
import { RealEstateType } from '../../@types/realEstateType'
import { enrichLead } from '../../store/lead/thunks'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { reportTrackingEvent } from '../../tracking/thunks'
import { useReportViewEvent } from '../../hooks'
import { FurnishingStepIcon } from '../../components/icons/StepIcons/FurnishingStepIcon'
import { IntegrationType } from '../../@types/config'
import { ReduxState } from '../../store/reducers'
import { richValuationFlowProgress } from '../../progress/valuationProgressHelper'
import { STEP } from '../../@types/state'

interface FurnishingTypeStepProps {
  goToNextStep: () => unknown
  goToPreviousStep: () => unknown
  goToErrorStep: () => unknown
}

export const furnishingTypeButtons = {
  [Furnishing.SIMPLE]: 'Einfach',
  [Furnishing.NORMAL]: 'Normal',
  [Furnishing.HIGH_QUALITY]: 'Gehoben',
  [Furnishing.LUXURIOUS]: 'Luxus',
}

const getHeadline = (realEstateType?: RealEstateType) => {
  switch (realEstateType) {
    case RealEstateType.APARTMENT:
      return 'Wie ist die Ausstattung deiner Wohnung?'
    case RealEstateType.HOUSE:
      return 'Wie ist die Ausstattung deines Hauses?'
    default:
      return ''
  }
}

export const FurnishingStep: FC<FurnishingTypeStepProps> = (props) => {
  const {
    config: { theme, integrationType },
    lead: { id: leadId, realEstateType },
  } = useSelector((state: ReduxState) => state)
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24
  const valuationBarProgress = richValuationFlowProgress(STEP.FURNISHING_TYPE)

  const dispatch = useDispatch()

  const goToNextStep = (furnishingType: Furnishing) => {
    if (!leadId) {
      console.error('Lead id prop does not exist')
      props.goToErrorStep()
      return
    }
    dispatch(updateFurnishingType(furnishingType))
    dispatch(
      enrichLead({
        enrichValuationType: EnrichLeadValuationType.SINGLE_VALUATION,
        valuationForStep: STEP.RESPONSE,
        pageTitle: 'furnishing',
        request: {
          id: leadId,
          furnishing: furnishingType,
        },
      })
    )
    dispatch(
      reportTrackingEvent({
        pageTitle: 'furnishing',
        label: 'furnishing_submit',
        inputValue: furnishingType,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: furnishingType,
        },
      })
    )
    props.goToNextStep()
  }

  useReportViewEvent({ pageTitle: 'furnishing' })

  return (
    <StepTemplate
      headline={getHeadline(realEstateType)}
      icon={<FurnishingStepIcon />}
      displayNext={false}
      displayBack={true}
      goToPreviousStep={props.goToPreviousStep}
      valuationBarProgress={valuationBarProgress}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      <div data-testid='furnishing-type-container'>
        {Object.entries(furnishingTypeButtons).map(([furnishingType, FurnishingTypeLabel]) => (
          <Button
            key={furnishingType}
            onClick={() => goToNextStep(furnishingType as Furnishing)}
            className='margin-right-l margin-bottom-xl'
            text={FurnishingTypeLabel as string}
            backgroundColor={isIv24Integration ? theme?.secondary : theme.background}
            color={isIv24Integration ? '#FFFFFF' : theme.text}
            borderColor={theme?.secondary}
          />
        ))}
      </div>
    </StepTemplate>
  )
}

export default FurnishingStep
